"use client";

import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useDateFormatter } from "react-aria";
import { chunk } from "remeda";

import { cn, swipePower, wrap } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { Button } from "~/components/ui/button";

import { type TourFromPage } from "../page";

type Direction = -1 | 1;

const swipeConfidenceThreshold = 10_000;

export function TripAdvisorReviewsCarousel({
  reviews,
}: {
  reviews: TourFromPage["tripadvisorReviews"];
}) {
  const chunks = chunk(reviews, 2);

  const formatter = useDateFormatter({ dateStyle: "long" });

  const [[page, direction], setPage] = useState([0, 0]);

  const index = wrap(0, chunks.length, page);

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  if (reviews.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-8 hidden w-full items-center justify-end gap-2 sm:flex">
        <div className="tabular-nums text-neutral-500">
          {index + 1}/{chunks.length}
        </div>
        <Button
          onClick={(event) => {
            event.preventDefault();
            paginate(-1);
          }}
          size="circle"
        >
          <span className="sr-only">Left</span>
          <AngleLeftIcon />
        </Button>
        <Button
          onClick={(event) => {
            event.preventDefault();
            paginate(1);
          }}
          size="circle"
        >
          <span className="sr-only">Right</span>
          <AngleRightIcon />
        </Button>
      </div>

      {/* Mobile */}
      <div className="-mx-6 overflow-hidden sm:hidden">
        <div className="flex snap-x snap-mandatory justify-between overflow-x-auto overflow-y-hidden">
          {reviews.map((review) => (
            <div
              className={cn(
                "box-content w-80 shrink-0 snap-start pb-6 pl-6 last:pr-24",
              )}
              key={review.id}
            >
              <div className="flex flex-col gap-2" key={review.id}>
                <div className="min-w-0 max-w-none">
                  <TripadvisorRating value={review.rating} owl={false} />
                </div>
                <div className="font-medium">{review.title}</div>
                <div>{review.description}</div>
                <div className="flex items-center gap-2">
                  <div>
                    <div>{review.name}</div>
                    <div className="text-xs text-neutral-500">
                      {formatter.format(
                        parseDate(review.date).toDate(getLocalTimeZone()),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden w-full overflow-hidden sm:block">
        <AnimatePresence custom={direction} initial={false} mode="popLayout">
          <motion.div
            animate="center"
            className="grid grid-cols-2 gap-4 sm:gap-8"
            custom={direction}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            exit="exit"
            initial="initial"
            key={`chunk-${index}`}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            transition={{
              opacity: { duration: 0.2 },
              x: { damping: 30, stiffness: 300, type: "spring" },
            }}
            variants={{
              center: {
                opacity: 1,
                x: 0,
              },
              exit: (direction: Direction) => ({
                opacity: 0,
                x: direction === 1 ? "-20%" : "20%",
              }),
              initial: (direction: Direction) => ({
                opacity: 1,
                x: direction === 1 ? "30%" : "-30%",
              }),
            }}
          >
            {chunks[index]?.map((review) => (
              <div className="flex flex-col gap-2" key={review.id}>
                <div className="min-w-0 max-w-none">
                  <TripadvisorRating value={review.rating} owl={false} />
                </div>
                <div className="font-medium">{review.title}</div>
                <div>{review.description}</div>
                <div className="flex items-center gap-2">
                  <div>
                    <div className="font-medium">{review.name}</div>
                    <div className="text-xs text-neutral-500">
                      {formatter.format(
                        parseDate(review.date).toDate(getLocalTimeZone()),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
